import { Inject, Injectable } from '@angular/core';
import { onCLS, onFCP, onLCP, onINP, onTTFB, Metric } from 'web-vitals';
import _ from 'lodash';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';

import { ClientLoggerService } from '../services/client-logger.service';
import { PerformanceContext } from '../interfaces/mysynch.interfaces';

declare global {
  interface PerformanceMemory {
    jsHeapSizeLimit: number;
    totalJSHeapSize: number;
    usedJSHeapSize: number;
  }

  interface Performance {
    memory?: PerformanceMemory;
  }
}
@Injectable({ providedIn: 'root' })
export class PerformanceMetricsService {

  private previousUsedHeapSize: number | null = null;
  private customMetricsEnabled: boolean = true;
  private jsMetricsEnabled: boolean = true;
  private webVitalsEnabled: boolean = true;

  constructor(@Inject('ApiFactory') protected api: any,
              @Inject('MySynchFactory') private MSF:any,
               private http: HttpClient,
              @Inject(ClientLoggerService) private log:ClientLoggerService,) 
  {

  }

  async init() {
    // register all metrics
    onLCP(m => this.sendWebVitalMetric(m));
    onFCP(m => this.sendWebVitalMetric(m));
    onCLS(m => this.sendWebVitalMetric(m));
    onINP(m => this.sendWebVitalMetric(m));
    onTTFB(m => this.sendWebVitalMetric(m));
    await this.setConfigRefs();
  }

  private async setConfigRefs(): Promise<void> {
      try {
        const config: any = await firstValueFrom(this.http.get('/api/config'));
        this.customMetricsEnabled = config.customMetricsEnabled ?? true;
        this.webVitalsEnabled = config.webVitalsEnabled ?? true;
        this.jsMetricsEnabled = config.jsMetricsEnabled ?? true;
        console.log('Performance Metrics Config:', {
          customMetricsEnabled: this.customMetricsEnabled,
          webVitalsEnabled: this.webVitalsEnabled,
          jsMetricsEnabled: this.jsMetricsEnabled
        });
        this.log.info(`Performance Metrics configuration loaded: ${JSON.stringify({
          customMetricsEnabled: this.customMetricsEnabled,
          webVitalsEnabled: this.webVitalsEnabled,
          jsMetricsEnabled: this.jsMetricsEnabled
        })}`);
      } catch (error) {
        console.error('Failed to load config for Performance Metrics', error);
        this.log.warn('Failed to load config for Performance Metrics, using defaults', { error: error.toString() });
      }
  }

  getUsedHeapSizeMB(): number | null {
    const perf = window.performance as Performance & { memory?: PerformanceMemory };
    if (perf.memory && perf.memory.usedJSHeapSize) {
      // Convert bytes to MB
      return Math.round(perf.memory.usedJSHeapSize / 1048576); // 1024 * 1024
    }
    return null; // Not supported in non-Chrome browsers
  }
  getRoute() {
    let rawRoute = window.location.hash;
    const route = rawRoute.split('?')[0];
    return route;
  }

  getPagePayload(metricName, value) {
    return {
      metricName: metricName,
      value: value,
      route: this.getRoute(),
      clientTimestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    };
  }

  getComponentPayload(metricName, value) {
     return {
      metricName: metricName,
      value: value,
      route: null,
      clientTimestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    };
  }

  getPayload(componentType: string, metricName: string, value: number) {
    switch(componentType) {
      case 'page':
        return this.getPagePayload(metricName, value);
      case 'component':
        return this.getComponentPayload(metricName, value);
      default:
        console.warn(`[Perf] Unknown metric type: ${componentType}`);
        this.log.warn(`[Perf] Unknown metric type: ${componentType}`);
        return null;
    }
  }

  getMark(name: string) {
    const mark = performance.getEntriesByName(name)[0];
    if (!mark) {
      console.warn(`[Perf] No mark found: ${name}`);
      this.log.warn(`[Perf] No mark found: ${name}`);
    }
    return mark;
  }
  getMeasure(name: string) {
    const measure = performance.getEntriesByName(name)[0];
    if (!measure) {
      console.warn(`[Perf] No measure found: ${measure}`);
      this.log.warn(`[Perf] No measure found: ${name}`);
    }
    return measure;
  }

  clearMarks(name: string) {
    performance.clearMarks(name); 
  }
  clearMeasures(name: string) {
    performance.clearMeasures(name);
  }

  measure(name: string, startMarkName: string, endMarkName: string) {
    performance.measure(name, startMarkName, endMarkName);
  }

  measureAndSend(componentType: string, metricName: string, startMarkName: string, endMarkName: string, perfContext: PerformanceContext | null = null) {
    const startMark = this.getMark(startMarkName);
    const endMark = this.getMark(endMarkName);

    if (startMark && endMark) {
      this.measure(metricName, startMarkName, endMarkName);
      const metricValue = this.getMeasure(metricName)?.duration;
      if (metricValue !== undefined) {
        const payload = this.getPayload(componentType, metricName, metricValue);
      //  console.log(`[Perf] ${metricName}: ${metricValue}ms`, payload, perfContext);

        //temp
        // this.clearMarks(startMarkName);
        //   this.clearMarks(endMarkName);
        //   this.clearMeasures(metricName);
        //   return;
        
        this.api.sendPeformanceMetrics(JSON.stringify(payload), perfContext, (err, response) => {
          if (err) {
            console.error('Error sending performance metric:', err);
            this.log.error('Error sending performance metric', { error: err.toString() });
          } else {
          //  console.log('Performance metric sent successfully:', response);
          }
          
          // Clear marks and measures after sending
          this.clearMarks(startMarkName);
          this.clearMarks(endMarkName);
          this.clearMeasures(metricName);
        });
      }
    }

  }
  
  markPageReady(metricName: string, perfContext: PerformanceContext | null = null) {
    if(!this.customMetricsEnabled){return;}
  requestAnimationFrame(() => {
    if ('requestIdleCallback' in window) {
      //console.log(`[Perf] Using requestIdleCallback for ${metricName}`);
      requestIdleCallback(() => {
        if(!_.isNull(metricName) && !_.isUndefined(metricName)) {
          this.endTiming("page", metricName, perfContext);
          const metricNameParts = metricName.split(':');
          const pageName = _.get(metricNameParts,"[1]", "UnknownPage");
          this.logWatcherCount(`Watchers:${pageName}:AfterRender`);
          this.logScopeCount(`Scopes:${pageName}:AfterRender`);
          this.logMemoryUsage(`Memory:${pageName}:AfterRender`);
          this.logHeapGrowthDelta(`Memory:${pageName}:AfterRender:HeapGrowth`);
          
        }
      }, { timeout: 200 });
    } else {
     // console.log(`[Perf] Using setTimeout for ${metricName}`);
      setTimeout(() => this.endTiming("page", metricName, perfContext), 100);
    }
  });
}


  startTiming(metricName: string) {
    if(!this.customMetricsEnabled){return;}
    performance.mark(`${metricName}-start`);
  }

  endTiming(componentType: string, metricName: string, perfContext: PerformanceContext | null = null) {
    if(!this.customMetricsEnabled){return;}
    performance.mark(`${metricName}-end`);
    this.measureAndSend(componentType, metricName, `${metricName}-start`, `${metricName}-end`, perfContext);
  }

  sendWebVitalMetric(metric: Metric) {
    if(!this.webVitalsEnabled) { return; }
    const payload = this.getPagePayload(metric.name, metric.value);
    this.api.sendPeformanceMetrics(JSON.stringify(payload), null, (err, response)=>{
        if (err) {
            console.error('Error sending performance metric:', err);
            this.log.error('Error sending performance metric', { error: err.toString() });
        } else {
     //       console.log('Performance metric sent successfully:', response);
        }
    });
  }
  logHeapGrowthDelta(metricName: string) {
    if(!this.jsMetricsEnabled){return;}
    if ('memory' in performance){

        const current = performance.memory.usedJSHeapSize;
        if (this.previousUsedHeapSize !== null) {
          const delta = (current - this.previousUsedHeapSize) / 1048576;
          const deltaPayload = this.getPagePayload(metricName, delta);
          this.api.sendPeformanceMetrics(JSON.stringify(deltaPayload), null, (err, response) => {
            if (err) {
              console.error('Error sending heap growth delta metric:', err);
              this.log.error('Error sending heap growth delta metric', { error: err.toString() });
            } else {
         //     console.log('Heap growth delta metric sent successfully:', response);
            }
          });
        }
        this.previousUsedHeapSize = current;
      
      }else{
        console.warn('Memory API not supported in this browser.');
      }
  }
  logMemoryUsage(metricName: string) {
    if(!this.jsMetricsEnabled){return;}
    if ('memory' in performance) {
      const mem = performance.memory;
      const usedMem = mem.usedJSHeapSize / 1048576 // Convert to MB From bytes
      const totalMem = mem.totalJSHeapSize / 1048576; // Convert to MB From bytes
      const usagePayload = this.getPagePayload(metricName, usedMem);
      this.api.sendPeformanceMetrics(JSON.stringify(usagePayload), null, (err, response) => {
        if (err) {
          console.error('Error sending memory usage metric:', err);
          this.log.error('Error sending memory usage metric', { error: err.toString() });
        } else {
         // console.log('Memory usage metric sent successfully:', response);
        }
      });

      const totalPayload = this.getPagePayload(`${metricName}:Total`, totalMem);
      this.api.sendPeformanceMetrics(JSON.stringify(totalPayload), null, (err, response) => {
        if (err) {
          console.error('Error sending total memory metric:', err);
          this.log.error('Error sending total memory metric', { error: err.toString() });
        } else {
        //  console.log('Total memory metric sent successfully:', response);
        }
      });
    } else {
      console.warn('Memory API not supported in this browser.');
      this.log.warn('Memory API not supported in this browser.');
    }
  }
  logWatcherCount(metricName: string): void {
    if(!this.jsMetricsEnabled){return;}
    if(!this.MSF.isLocalOrDevelopmentEnvironment()){return;}
    try {
      const count = this.MSF?.getLocalDevWatcherCount();
      if (typeof count === 'number') {
       // console.log(`[Perf] ${metricName}: ${count}`);
        const payload = this.getPagePayload(metricName, count);
        this.api.sendPeformanceMetrics(JSON.stringify(payload), null, (err, response) => {
          if (err) {
            console.error('Error sending watcher count metric:', err);
            this.log.error('Error sending watcher count metric', { error: err.toString() });
          } else {
         //   console.log('Watcher count metric sent successfully:', response);
          }
        });
      }
    } catch (err) {
      console.warn(`[Perf] Failed to read watcher count for ${metricName}`, err);
      this.log.warn(`[Perf] Failed to read watcher count for ${metricName}`, { error: err.toString() });
    }
  }
  logScopeCount(metricName: string): void {
    if(!this.jsMetricsEnabled){return;}
    if(!this.MSF.isLocalOrDevelopmentEnvironment()){return;}
    try {
      const count = this.MSF?.getScopeCount();
      if (typeof count === 'number') {
        //console.log(`[Perf] ${metricName}: ${count}`);
        const payload = this.getPagePayload(metricName, count);
        this.api.sendPeformanceMetrics(JSON.stringify(payload), null, (err, response) => {
          if (err) {
            console.error('Error sending scope count metric:', err);
            this.log.error('Error sending scope count metric', { error: err.toString() });
          } else {
          //  console.log('Scope count metric sent successfully:', response);
          }
        });
      }
    } catch (err) {
      console.warn(`[Perf] Failed to read scope count for ${metricName}`, err);
      this.log.warn(`[Perf] Failed to read scope count for ${metricName}`, { error: err.toString() });
    }
  }

}

